import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import {Event} from './Event';

export const packWeekEvents = (segments, heights, gap = 16) => {
  const bottoms = Array(7).fill(0);
  const positioned = segments.map(segment => {
    const top = Math.max(...bottoms.slice(segment.dayIndex, segment.dayIndex + segment.span));
    // Used only until the card has been measured on its first layout pass.
    const height = heights[segment.key] || 70;
    for (let day = segment.dayIndex; day < segment.dayIndex + segment.span; day++) {
      bottoms[day] = top + height + gap;
    }
    return {...segment, top, height};
  });
  return {segments: positioned, height: Math.max(0, ...bottoms) - (positioned.length ? gap : 0)};
};

export default function WeekEvents({week, weekIndex, gridRow, className, edit, onDelete, compact}) {
  const nodes = useRef(new Map());
  const [heights, setHeights] = useState({});
  const segments = useMemo(() => week.flatMap((day, dayIndex) => day.events.flatMap((event, lane) => {
    if (event.spacer || (!event.start && dayIndex !== 0)) return [];
    return [{key: `${event._id}-${dayIndex}-${lane}`, event, lane, dayIndex,
      span: Math.min(event.dayDistance - event.day, 7 - dayIndex)}];
  })).sort((a, b) => a.lane - b.lane || a.dayIndex - b.dayIndex), [week]);

  useLayoutEffect(() => {
    const measure = () => {
      const next = {};
      nodes.current.forEach((node, key) => {
        const height = Math.ceil(node.getBoundingClientRect().height);
        if (height > 0) next[key] = height;
      });
      setHeights(previous => Object.keys(previous).length === Object.keys(next).length &&
        Object.keys(next).every(key => previous[key] === next[key]) ? previous : next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    nodes.current.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, [segments]);

  const layout = packWeekEvents(segments, heights, compact ? 8 : 16);
  return <div className={className} style={{gridColumn: '1 / -1', gridRow, position: 'relative', height: layout.height}}>
    {layout.segments.map(segment => <div key={segment.key} data-calendar-event={segment.event._id}
      ref={node => {if (node) nodes.current.set(segment.key, node); else nodes.current.delete(segment.key);}}
      style={{gridColumn: `${segment.dayIndex + 1} / ${segment.dayIndex + segment.span + 1}`,
        position: 'absolute', top: segment.top, left: 2, right: 2, minWidth: 0}}>
      <Event event={segment.event} edit={edit} onDelete={onDelete} compact={compact} dateIndex={weekIndex * 7 + segment.dayIndex} />
    </div>)}
  </div>;
}
